// Sawyer 09 pet-inactivity (D1)
/**
 * The "Show inactive dogs" control shared by /compliance and Reports →
 * Vaccination Compliance, so the two lists cannot word it differently
 * (Design Philosophy #1 one glance, one truth).
 *
 * Renders NOTHING when `months` is null: the rule is off, or the server is
 * older and sent no inactivity fields (deploy-window guard). Otherwise a
 * plain-text caption that says which dogs the list contains (a printout or
 * screenshot carries it, AC25) and a 44px checkbox row described by it.
 *
 * Principles: #1 one truth (one caption, both lists), #5 never lie about
 * data (the hidden count is stated, not implied), #7 built for hands (44px
 * label row, visible focus ring).
 *
 * Counter Test: Sawyer, with a PACFA inspector beside him, reads "Active
 * dogs only. 3 inactive dogs hidden (no visit in 6 months)." and taps the
 * box when the inspector asks to see everything.
 */
import { Checkbox } from '@ferro/ui';
import './inactive-dogs-toggle.css';

export interface InactiveDogsToggleProps {
  months: number | null | undefined;
  hiddenCount: number;
  includeInactive: boolean;
  onChange(next: boolean): void;
  busy: boolean;
  /** Unique per page so two toggles never share an id. */
  idPrefix: string;
}

export function inactiveCaption(months: number, hiddenCount: number, includeInactive: boolean): string {
  if (includeInactive) return 'Including inactive dogs.';
  return `Active dogs only. ${hiddenCount} inactive ${hiddenCount === 1 ? 'dog' : 'dogs'} hidden (no visit in ${months} ${
    months === 1 ? 'month' : 'months'
  }).`;
}

export function InactiveDogsToggle({
  months,
  hiddenCount,
  includeInactive,
  onChange,
  busy,
  idPrefix,
}: InactiveDogsToggleProps): JSX.Element | null {
  if (months === null || months === undefined) return null;
  const captionId = `${idPrefix}-inactive-caption`;
  return (
    <div className="ferro-inactive-toggle" data-testid="inactive-dogs-toggle">
      <p className="ferro-inactive-toggle__caption" id={captionId} data-testid="inactive-dogs-caption">
        {inactiveCaption(months, hiddenCount, includeInactive)}
      </p>
      <Checkbox
        id={`${idPrefix}-show-inactive`}
        name={`${idPrefix}-show-inactive`}
        label="Show inactive dogs"
        checked={includeInactive}
        // Not `disabled`: a disabled control drops keyboard focus mid-reload
        // (AC28). While the list reloads it stays focusable, says it is busy,
        // and ignores a second change so two requests cannot race.
        aria-disabled={busy || undefined}
        aria-describedby={captionId}
        onChange={(e) => {
          if (!busy) onChange(e.currentTarget.checked);
        }}
        data-testid="show-inactive-dogs"
      />
    </div>
  );
}
